import moment from "moment";
import {
  TableCellType,
  TableHeader,
} from "../../../../../components/custom-table/table";
import { AuthStatusOptionModel } from "../../../../../data-model/authStatusOptions";
import { MedicalPaOrder } from "../../../../../data-model/medicalPaOrdersModel";
import { EVWriteBackStatus } from "../../../../../enums/evBvWriteBackStatus";
import { generateDataForDocWithSideNavModalFromSingleMedicalPaOrder } from "../../../../../utils/createDataForDocWithSideNavModal";
import { generateFullName } from "../../../../../utils/generateOrderDataForTable";
import { getAuthStatusForTables } from "../../../../../utils/getAuthStatusOfMedicalPaOrder";
import { rotateLastSegmentFirst } from "../../../../../utils/getFullNameOfTheProvider";
import { getScreenshotValues } from "../../../../../utils/getScreenshotValues";
import { capitalizeString } from "../../../../../utils/stringModifications";

export enum AllPaOrdersForExternalTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  CREATED_AT = "createdAt",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  PROVIDER = "provider",
  DATE_OF_SERVICE = "dateOfService",
  BO_VALUE = "boValue",
  AUTH_VERIFICATION = "authVerification",
  RPA_STATUS = "rpaStatus",
  DOCUMENTS_LIST = "documentsList",
  ID = "id",
  ROW_DATA = "rowData",
}
export const AllPaOrdersForExternalTableHeaders: TableHeader[] = [
  {
    label: "Patient Name",
    key: AllPaOrdersForExternalTableColumnKeys.PATIENT_DETAILS,
    order: 1,
    width: 15,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Date of Work",
    key: AllPaOrdersForExternalTableColumnKeys.CREATED_AT,
    order: 2,
    width: 11,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: "Plan Details",
    key: AllPaOrdersForExternalTableColumnKeys.PLAN_DETAILS,
    order: 3,
    width: 15,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Medication",
    key: AllPaOrdersForExternalTableColumnKeys.MEDICATION,
    order: 4,
    width: 10,
    sortable: true,
    type: TableCellType.STRING_WITH_CUSTOM_TOOLTIP,
  },
  {
    label: "Provider",
    key: AllPaOrdersForExternalTableColumnKeys.PROVIDER,
    order: 5,
    width: 15,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "DOS",
    key: AllPaOrdersForExternalTableColumnKeys.DATE_OF_SERVICE,
    order: 6,
    width: 8,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: "BO Value",
    key: AllPaOrdersForExternalTableColumnKeys.BO_VALUE,
    order: 7,
    width: 9,
    type: TableCellType.BADGE,
  },
  {
    label: "Auth Status",
    key: AllPaOrdersForExternalTableColumnKeys.AUTH_VERIFICATION,
    order: 8,
    width: 9,
    type: TableCellType.BADGE_WITH_I,
  },
  {
    label: "",
    key: AllPaOrdersForExternalTableColumnKeys.RPA_STATUS,
    order: 10,
    width: 8,
    type: TableCellType.RPA_STATUS_FOR_EXTERNAL,
  },
];

export const generateAllPaOrdersForExternalTableData = (
  medicalPaOrders: MedicalPaOrder[],
  authStatusOptions: AuthStatusOptionModel[],
) => {
  return medicalPaOrders.map((medicalPaOrder) => {
    const authStatus = getAuthStatusForTables(
      medicalPaOrder,
      authStatusOptions,
    );
    const { displayText, tooltipText } =
      generateToolTipAndCellDataFromJcode(medicalPaOrder);
    return {
      [AllPaOrdersForExternalTableColumnKeys.PATIENT_DETAILS]: {
        mainText: capitalizeString(
          generateFullName(
            medicalPaOrder?.demographics?.first_name ?? "",
            "",
            medicalPaOrder?.demographics?.last_name ?? "",
          ),
        ),
        secondaryText: `MRN : ${medicalPaOrder?.demographics?.patient_mrn ?? ""}`,
      },
      [AllPaOrdersForExternalTableColumnKeys.DATE_OF_SERVICE]: moment(
        medicalPaOrder?.payload?.date_of_service,
      ).format("MM/DD/YYYY"),
      [AllPaOrdersForExternalTableColumnKeys.PLAN_DETAILS]: {
        mainText: medicalPaOrder?.coverage?.primary?.payer_name ?? "",
        secondaryText: medicalPaOrder?.coverage?.primary?.member_id ?? "",
      },
      [AllPaOrdersForExternalTableColumnKeys.MEDICATION]: {
        displayText,
        tooltipText,
      },
      [AllPaOrdersForExternalTableColumnKeys.ROW_DATA]:
        JSON.stringify(medicalPaOrder),
      [AllPaOrdersForExternalTableColumnKeys.ID]: medicalPaOrder?.id ?? "",
      [AllPaOrdersForExternalTableColumnKeys.CREATED_AT]: moment(
        medicalPaOrder?.created_at,
      ).format("MM/DD/YYYY"),
      [AllPaOrdersForExternalTableColumnKeys.AUTH_VERIFICATION]: {
        text: authStatus?.text ?? "",
        color: authStatus?.textColor ?? "",
        bgColor: authStatus?.bgColor ?? "",
        showInfoIcon:
          authStatus?.id === "denied" || authStatus?.id === "denied_by_risa",
        displayText: getToolTipTextForAuthStatus(medicalPaOrder),
      },
      [AllPaOrdersForExternalTableColumnKeys.BO_VALUE]: {
        text: medicalPaOrder?.status?.bo_status ?? "Required",
        color: "#0f0f0f",
        bgColor: "#f0f0f0",
      },
      [AllPaOrdersForExternalTableColumnKeys.RPA_STATUS]: {
        status:
          medicalPaOrder?.status?.ev_write_back_status ??
          EVWriteBackStatus.NOT_INITIATED,
        filePaths: getScreenshotValues(
          medicalPaOrder?.auth_on_file?.screenshots
            ?.write_note_cig_box_screenshot,
        ),
        docList:
          generateDataForDocWithSideNavModalFromSingleMedicalPaOrder(
            medicalPaOrder,
          ),
      },
      [AllPaOrdersForExternalTableColumnKeys.PROVIDER]: rotateLastSegmentFirst(
        medicalPaOrder?.payload?.practitioner_name ?? "",
      ),
    };
  });
};

export const getFilePathsForModal = (orderData: MedicalPaOrder) => {
  const files =
    orderData?.auth_on_file?.screenshots?.document_upload_screenshots;
  if (!files) return [];
  return files.map((file) => file.screenshot_path);
};

export const generateToolTipAndCellDataFromJcode = (
  orderData: MedicalPaOrder,
) => {
  const allJCodesData = orderData?.auth_on_file?.auth_entries
    ?.map((authEntry) => authEntry.j_codes)
    .filter((jCode) => jCode && jCode?.length > 0)
    .flat();

  const displayText = allJCodesData
    ?.map((jCode) => {
      return jCode?.description ?? "";
    })
    .join(", ");
  const tooltipText = allJCodesData
    ?.map((jCode) => {
      return (jCode?.j_code ?? "") + " - " + (jCode?.description ?? "");
    })
    .join(", ");

  return {
    displayText,
    tooltipText,
  };
};

export const getToolTipTextForAuthStatus = (orderData: MedicalPaOrder) => {
  const auditTrails = orderData?.auth_status_changes;
  if (auditTrails && auditTrails?.length > 0) {
    const deniedByRisa = auditTrails
      .filter(
        (auditTrail) =>
          auditTrail?.new_value === "denied_by_risa" ||
          auditTrail?.old_value === "denial_by_risa",
      )
      .sort(
        (a, b) =>
          new Date(b?.updated_at ?? "").getTime() -
          new Date(a?.updated_at ?? "").getTime(),
      );
    const deniedByRisaText = deniedByRisa[0]?.comment ?? "";
    return deniedByRisaText;
  } else {
    return "";
  }
};
